import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import EntityProfileLink from '../../watchlist/EntityProfileLink'
import { Badge } from '../../ui'
import type { NetworkConnection, NetworkTier } from '../../../types/network'
import {
  EVENT_LABEL,
  companyReason,
  connectionSpan,
  periodLabel,
  roleLabel,
} from './networkText'

export type NetworkTime = 'now' | 'all'

const TIERS: Array<{ id: NetworkTier; label: string; shown: number }> = [
  { id: 'close', label: 'Tætte partnere', shown: 50 },
  { id: 'working', label: 'Samarbejdspartnere', shown: 10 },
  { id: 'peripheral', label: 'Perifere', shown: 5 },
]

const DOTS: Record<NetworkTier, number> = { close: 3, working: 2, peripheral: 1 }

/**
 * Connections grouped by how close they are. »Nu« shows the ties that
 * still hold, with former ones folded into »Tidligere«; »Alle år« mixes
 * them in by strength.
 */
const NetworkList: React.FC<{
  connections: NetworkConnection[]
  time: NetworkTime
}> = ({ connections, time }) => {
  const visible =
    time === 'now' ? connections.filter(c => c.current) : connections
  const former = time === 'now' ? connections.filter(c => !c.current) : []

  if (connections.length === 0) {
    return (
      <p className="rounded-[10px] border border-rule bg-paper px-5 py-6 text-sm text-ink-500">
        Ingen andre personer i personens selskaber. Revisorer tæller ikke med.
      </p>
    )
  }

  return (
    <div
      className="overflow-hidden rounded-[10px] border border-rule bg-paper"
      data-testid="network-list"
    >
      {TIERS.map(t => (
        <Group
          key={t.id}
          label={t.label}
          shown={t.shown}
          rows={visible.filter(c => c.tier === t.id)}
        />
      ))}
      {visible.length === 0 && (
        <p className="px-5 py-4 text-sm text-ink-500">
          Ingen aktuelle forbindelser.
        </p>
      )}
      {former.length > 0 && (
        <Group label="Tidligere" shown={0} rows={former} folded />
      )}
    </div>
  )
}

function Group({
  label,
  rows,
  shown,
  folded = false,
}: {
  label: string
  rows: NetworkConnection[]
  shown: number
  folded?: boolean
}) {
  const [showAll, setShowAll] = useState(false)
  if (rows.length === 0) return null
  const visible = showAll ? rows : rows.slice(0, shown)
  const rest = rows.length - visible.length
  const id = `net-${label.toLowerCase().replace(/\W+/g, '-')}`
  return (
    <section
      aria-labelledby={id}
      className="border-t border-rule-soft first:border-t-0"
    >
      <h3
        id={id}
        className="px-5 pb-1.5 pt-3 text-[12px] font-semibold text-ink-500"
      >
        {label} <span className="tabular-nums">· {rows.length}</span>
      </h3>
      {visible.length > 0 && (
        <ul className="divide-y divide-rule-soft">
          {visible.map(c => (
            <ConnectionRow key={c.enhedsNummer} c={c} />
          ))}
        </ul>
      )}
      {rest > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="w-full border-t border-rule-soft px-5 py-2.5 text-left text-[13px] font-semibold text-ink-500 hover:text-ink"
        >
          {folded && visible.length === 0
            ? `Vis ${rest} tidligere`
            : `Vis ${rest} mere`}
        </button>
      )}
    </section>
  )
}

function ConnectionRow({ c }: { c: NetworkConnection }) {
  const [open, setOpen] = useState(false)
  const top = c.companies[0]
  const span = connectionSpan(c)
  const detailsId = `net-person-${c.enhedsNummer}`
  return (
    <li className="px-5 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <EntityProfileLink
              to={`/person/${c.enhedsNummer}`}
              entityType="person"
              entityId={String(c.enhedsNummer)}
              className="font-bold text-ink hover:underline decoration-ink-300 underline-offset-[3px]"
            >
              {c.navn || 'Uden navn'}
            </EntityProfileLink>
            {c.via && (
              <span className="text-[12px] text-ink-400">via {c.via.name}</span>
            )}
            {c.collapsedShared > 0 && (
              <Badge tone="signal">
                {c.collapsedShared}{' '}
                {c.collapsedShared === 1 ? 'konkurs' : 'konkurser'}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-[13px] leading-snug text-ink-500">
            {top ? companyReason(top) : ''}
            {span && <span className="tabular-nums"> · {span}</span>}
          </p>
        </div>
        <Strength tier={c.tier} />
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          aria-controls={detailsId}
          aria-label={`Detaljer om ${c.navn}`}
          className="-mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-400 hover:bg-rule-soft hover:text-ink"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {open && (
        <div
          id={detailsId}
          className="mt-2.5 space-y-2 border-l-2 border-rule pl-3"
        >
          {c.events.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {c.events.map(e => (
                <Badge
                  key={e}
                  tone={e === 'left_before_collapse' ? 'signal' : 'neutral'}
                >
                  {EVENT_LABEL[e]}
                </Badge>
              ))}
            </div>
          )}
          <ul className="space-y-1">
            {c.companies.map(s => (
              <li key={s.cvr} className="text-[13px] leading-snug text-ink-700">
                <EntityProfileLink
                  to={`/company/${s.cvr}`}
                  entityType="company"
                  entityId={s.cvr}
                  className="font-semibold text-ink hover:underline decoration-ink-300 underline-offset-2"
                >
                  {s.name}
                </EntityProfileLink>
                <span className="text-ink-500">
                  {' · '}
                  {roleLabel(s.role)}
                  {s.via && ` via ${s.via.name}`}, personen{' '}
                  {roleLabel(s.subjectRole)}
                  {' · '}
                  <span className="tabular-nums">
                    {s.together
                      ? periodLabel(s.from, s.to, s.current)
                      : 'ikke samtidig'}
                  </span>
                </span>
                {s.collapsed && (
                  <Badge tone="signal" className="ml-1.5">
                    Konkurs/tvangsopløst
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  )
}

function Strength({ tier }: { tier: NetworkTier }) {
  return (
    <StrengthBars
      level={DOTS[tier]}
      label={TIERS.find(t => t.id === tier)?.label}
    />
  )
}

/** Three rising bars, like signal strength — not three dots, which read as a menu. */
export function StrengthBars({
  level,
  label,
}: {
  level: number
  label?: string
}) {
  return (
    <span
      className="mt-1 inline-flex h-3 shrink-0 items-end gap-[2px]"
      title={label}
      aria-label={`Styrke ${level} af 3`}
      role="img"
    >
      {[6, 9, 12].map((h, i) => (
        <span
          key={h}
          className={`w-[3px] rounded-[1px] ${i < level ? 'bg-ink' : 'bg-rule-strong'}`}
          style={{ height: h }}
        />
      ))}
    </span>
  )
}

export default NetworkList
